import createHttp from '@/config/http';
import type {
	AccountData,
	BVProductsResponse,
	ClinicProducts,
	ClinicProductsResponse,
	ProductsWithSizesResponse,
	ProductWithSizes,
	UtilityProduct,
	UtilityProductsResponse,
} from '@/types/product';
import type { ResponseMetaData } from '@/types/service';

const httpBV = createHttp(import.meta.env.VITE_BV_URL);
const httpOrders = createHttp(import.meta.env.VITE_ORDERS_URL);
const httpCommon = createHttp(import.meta.env.VITE_COMMON_URL);

export const getProductsByAccountBV = async (
	clinicId: string,
	userPort: string,
	clinics?: string,
): Promise<{
	items: AccountData[];
	meta: ResponseMetaData;
}> => {
	const response = await httpBV.get<BVProductsResponse>(
		`/products/by-account/${clinicId}/${userPort}`,
		{
			params: {
				clinics
			}
		}
	);
	return {
		items: response.data.data.items,
		meta: response.data.data.metaData,
	};
};

export const getProductsWithSizes = async (
	clinicId: string,
	userPort: string,
): Promise<{
	items: ProductWithSizes[];
	meta: ResponseMetaData;
}> => {
	const response = await httpOrders.get<ProductsWithSizesResponse>(
		`/products/with-sizes/${clinicId}/${userPort}`,
	);
	return {
		items: response.data.data.items,
		meta: response.data.data.metaData || {
			totalRecords: 0,
			currentPage: 1,
			itemsPerPage: 0,
			totalPages: 0,
		},
	};
};

export const getProductsWithSizesByClinics = async (
	clinicIds: string[],
	userPort: string,
): Promise<{
	items: ClinicProducts;
	meta: ResponseMetaData;
}> => {
	const response = await httpOrders.post<ClinicProductsResponse>(
		`/products/with-sizes/${userPort}`,
		{
			clinicIds,
		},
	);
	// The API returns a single map object { [clinicId]: ProductWithSizes[] }
	// IResponseFetch types it as items: T[], but the actual value is the map itself.
	const rawItems = response.data.data.items;
	const clinicMap: ClinicProducts = Array.isArray(rawItems)
		? Object.assign({}, ...rawItems)
		: (rawItems as unknown as ClinicProducts) ?? {};
	return {
		items: clinicMap,
		meta: response.data.data.metaData || {
			totalRecords: 0,
			currentPage: 1,
			itemsPerPage: 0,
			totalPages: 0,
		},
	};
};

export const getUtilityProducts = async (
	clinicId: string,
	userPort: string,
): Promise<{
	items: UtilityProduct[];
	meta: ResponseMetaData;
}> => {
	if (!clinicId || !userPort) {
		return {
			items: [],
			meta: {
				currentPage: 0,
				totalRecords: 0,
				itemsPerPage: 0,
				totalItems: 0,
				totalPages: 0,
			},
		};
	}
	const response = await httpCommon.get<UtilityProductsResponse>(
		`/products/utility/${clinicId}/${userPort}`,
	);
	return {
		items: response.data.data.items,
		meta: response.data.data.metaData,
	};
};

//This will be used in Grafts to assign feature
export const getProductSizes = async (product: string) => {
	const response = await httpCommon.get(
		`/grafts-to-assign/product-sizes?product=${product}`,
	);
	return {
		items: response.data.data,
		meta: response.data.data.metaData,
	};
};
